import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { t } from 'i18next';
import { HiBellAlert, HiMagnifyingGlass } from 'react-icons/hi2';
import { LuChevronLeft, LuChevronRight, LuRefreshCw } from 'react-icons/lu';
import { fetchAllNotifications } from 'src/services/notifications';
import usePagePermission from 'src/hooks/usePagePermission';
import { useNavigate } from 'react-router';
import './notification.css';

const PER_PAGE = 10;

/**
 * Relative age ("2h"), with the exact timestamp kept beside it.
 *
 * A notification list is read for recency first — "how long has this been waiting on me" —
 * and the absolute time only matters once something looks wrong. Showing both saves the
 * reader doing the subtraction without hiding the fact.
 */
function relativeAge(value: string | Date): string {
  const then = new Date(value).getTime();
  if (!Number.isFinite(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  return `${Math.floor(days / 30)}mo`;
}

function formatWhen(value: string | Date): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : format(date, "yyyy-MM-dd 'at' hh:mm a");
}

const Notification = () => {
  // The notification list is its own page in the matrix; without this the screen was
  // the only live route in the app with no permission check at all.
  const { loading: permissionsLoading, notAllowed } = usePagePermission('Notifications');
  const [allNotification, setAllNotification] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  /** What the server is actually filtered by — see the debounce below. */
  const [appliedSearch, setAppliedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleNavigation = async (item: any) => {
    if (item?.type?.includes('Compliance Control Action') && item?.actionId) {
      const ids = String(item.actionId).split('/');
      if (ids.length >= 3) {
        const [complianceId, domainId, controlId] = ids;
        navigate(`/compliances/view-control/${controlId}/${complianceId}/${domainId}`);
      }
    } else if (item?.type?.includes('Public Survey')) {
      navigate('/surveys');
    } else if (item?.type?.includes('Workflow Approval') && item?.actionId) {
      navigate(`/approvals/${item.actionId}`);
    } else if (item?.type?.includes('Remediation') && item?.actionId) {
      // actionId is the plan's own id, so this is a plain detail link - unlike a
      // compliance action, whose id is the compliance/domain/control triple.
      navigate(`/remediation/${item.actionId}`);
    }
  };

  /** True when the row leads somewhere. One that does not must not look clickable. */
  const isActionable = (item: any): boolean => {
    const type = String(item?.type ?? '');
    if (type.includes('Public Survey')) return true;
    if (!item?.actionId) return false;
    if (type.includes('Compliance Control Action')) {
      return String(item.actionId).split('/').length >= 3;
    }
    return type.includes('Workflow Approval') || type.includes('Remediation');
  };

  const fetchNotificationsFromBackend = async () => {
    setLoading(true);
    try {
      const data = await fetchAllNotifications(PER_PAGE, currentPage + 1, appliedSearch);
      const rows = data?.data?.notifications ?? [];
      const count = Number(data?.data?.totalCount ?? 0);
      setAllNotification(Array.isArray(rows) ? rows : []);
      setTotalCount(count);
      setTotalPages(Math.ceil(count / PER_PAGE));
    } catch (error) {
      setAllNotification([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  /**
   * The search used to reach the API on every keystroke, from whatever page the reader
   * happened to be on — five characters meant five requests, and searching from page 3
   * could land on an empty page of a one-page result. Debounced, and always from page 1.
   */
  useEffect(() => {
    const id = window.setTimeout(() => {
      setAppliedSearch(searchTerm.trim());
      setCurrentPage(0);
    }, 400);
    return () => window.clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    fetchNotificationsFromBackend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, appliedSearch]);

  const rangeLabel = useMemo(() => {
    if (!totalCount) return '';
    const from = currentPage * PER_PAGE + 1;
    const to = Math.min((currentPage + 1) * PER_PAGE, totalCount);
    return `${from}–${to} / ${totalCount.toLocaleString()}`;
  }, [currentPage, totalCount]);

  if (permissionsLoading) {
    return <div className="d-flex justify-content-center p-5"><span className="spinner-border text-primary" /></div>;
  }

  if (notAllowed) {
    return (
      <div className="container-fluid p-3" style={{ paddingTop: '1.25rem' }}>
        <div className="alert alert-danger mb-0 text-center">
          You are not allowed to view this page
        </div>
      </div>
    );
  }

  return (
    <div className="root notifications-page">
      <Breadcrumb>
        <BreadcrumbItem>{t('Dashboard.You are here')}</BreadcrumbItem>
        <span className="mx-2">/</span>
        <BreadcrumbItem active>{t('Notification')}</BreadcrumbItem>
      </Breadcrumb>

      <div className="notifications-card">
        <div className="notifications-head">
          <div className="notifications-head-titles">
            <h5 className="notifications-title">{t('Notification')}</h5>
            {totalCount > 0 && (
              <span className="notifications-count">{totalCount.toLocaleString()}</span>
            )}
          </div>

          <div className="notifications-tools">
            <div className="notifications-search">
              <HiMagnifyingGlass size={15} aria-hidden />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={String(t('Search ...') || 'Search')}
                aria-label={String(t('Search ...') || 'Search')}
              />
            </div>
            <button
              type="button"
              className="notifications-refresh"
              onClick={() => fetchNotificationsFromBackend()}
              disabled={loading}
              title={String(t('Refresh') || 'Refresh')}
            >
              <LuRefreshCw size={15} aria-hidden />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="notifications-empty">{String(t('Loading') || 'Loading')}</div>
        ) : allNotification.length === 0 ? (
          <div className="notifications-empty">
            {appliedSearch
              ? String(t('No Data Available') || 'Nothing matches that search.')
              : String(t('No Data Available') || 'No notifications yet.')}
          </div>
        ) : (
          <ul className="notifications-list">
            {allNotification.map((item, index) => {
              const actionable = isActionable(item);
              return (
                <li
                  key={item?.id ?? index}
                  className={
                    'notifications-item' +
                    (actionable ? ' is-actionable' : '') +
                    (item?.seen ? '' : ' is-unread')
                  }
                  onClick={actionable ? () => handleNavigation(item) : undefined}
                  role={actionable ? 'button' : undefined}
                  tabIndex={actionable ? 0 : undefined}
                  onKeyDown={
                    actionable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleNavigation(item);
                          }
                        }
                      : undefined
                  }
                >
                  <span className="notifications-icon" aria-hidden>
                    <HiBellAlert size={16} />
                  </span>

                  <div className="notifications-body">
                    <div className="notifications-row-top">
                      <span className="notifications-type">{item?.type}</span>
                      <span className="notifications-age" title={formatWhen(item?.createdAt)}>
                        {relativeAge(item?.createdAt)}
                      </span>
                    </div>
                    <p className="notifications-message">{item?.message}</p>
                    <p className="notifications-when">{formatWhen(item?.createdAt)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {totalPages > 1 && (
          <div className="notifications-pager">
            <span className="notifications-range">{rangeLabel}</span>
            <div className="notifications-pager-buttons">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                disabled={currentPage === 0 || loading}
              >
                <LuChevronLeft size={14} aria-hidden />
                {String(t('Previous') || 'Previous')}
              </button>
              <span className="notifications-page-of">
                {currentPage + 1} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                disabled={currentPage >= totalPages - 1 || loading}
              >
                {String(t('Next') || 'Next')}
                <LuChevronRight size={14} aria-hidden />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notification;
